import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
	Box,
	Button,
	ColumnLayout,
	Container,
	FormField,
	Header,
	Input,
	RadioGroup,
	Select,
	SpaceBetween
} from '@cloudscape-design/components'

import FormInput from '../../ControlledFormInputs/Input'

import { useApprovalFormStore } from '../../../stores/gcpProjects/approvalFormStore'
import CONSTANTS from '../../../constants'
import {
	cidrRules,
	getSubnetCidrError,
	isGcpProjectGke,
	normalizeAdditionalSubnets,
	resolveGcpProjectInfrastructureType,
	validateSubnetDetailsForApproval
} from '../../../helper/gcpProjectSubnetValidation'
import type { AdditionalSubnet, gcpProject } from '../../../types/gcpProject'

type SubnetFormValues = {
	primarySubnet: string
	controlPlaneSubnet: string
	secondaryPodsRange: string
	secondaryServicesRange: string
}

const trimValue = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : ''

const resourceTypeOptions = CONSTANTS.SUBNET_RESOURCE_TYPE_OPTIONS

const GcpProjectSubnetDetails: FC<{
	gcpProject: gcpProject
	embeddedInApproval?: boolean
	onSubnetValidityChange?: (isValid: boolean) => void
}> = ({ gcpProject, embeddedInApproval = false, onSubnetValidityChange }) => {
	const details = gcpProject.details || {}
	const infrastructureType = resolveGcpProjectInfrastructureType(details)
	const isGke = isGcpProjectGke(details)
	const savedAdditionalSubnets = useMemo(
		() => normalizeAdditionalSubnets(details.additionalSubnets),
		[details.additionalSubnets]
	)
	const savedPrimarySubnetResourceType =
		typeof details.primarySubnetResourceType === 'string'
			? details.primarySubnetResourceType
			: ''
	const hasSavedSubnets =
		Boolean(trimValue(details.primarySubnet)) ||
		savedAdditionalSubnets.length > 0

	const [nonGkeAddSubnets, setNonGkeAddSubnets] = useState<'yes' | 'no'>(
		hasSavedSubnets ? 'yes' : 'no'
	)
	const [editableAdditionalSubnets, setEditableAdditionalSubnets] = useState<
		AdditionalSubnet[]
	>(savedAdditionalSubnets)
	const [
		editablePrimarySubnetResourceType,
		setEditablePrimarySubnetResourceType
	] = useState(savedPrimarySubnetResourceType)

	const {
		actions: { setFormValues }
	} = useApprovalFormStore()

	const defaultValues = useMemo(
		() => ({
			primarySubnet:
				typeof details.primarySubnet === 'string' ? details.primarySubnet : '',
			controlPlaneSubnet:
				typeof details.controlPlaneSubnet === 'string'
					? details.controlPlaneSubnet
					: '',
			secondaryPodsRange:
				typeof details.secondaryPodsRange === 'string'
					? details.secondaryPodsRange
					: '',
			secondaryServicesRange:
				typeof details.secondaryServicesRange === 'string'
					? details.secondaryServicesRange
					: ''
		}),
		[
			details.controlPlaneSubnet,
			details.primarySubnet,
			details.secondaryPodsRange,
			details.secondaryServicesRange
		]
	)

	const { control, reset, watch } = useForm<SubnetFormValues>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues
	})

	const watchedPrimarySubnet = watch('primarySubnet')
	const watchedControlPlaneSubnet = watch('controlPlaneSubnet')
	const watchedSecondaryPodsRange = watch('secondaryPodsRange')
	const watchedSecondaryServicesRange = watch('secondaryServicesRange')
	const primarySubnetLabel = isGke ? 'Node Subnet Range' : 'Primary Subnet'
	const showNonGkeSubnetFields = isGke || nonGkeAddSubnets === 'yes'

	const syncApprovalFormValues = useCallback(() => {
		setFormValues('primarySubnet', watchedPrimarySubnet)
		setFormValues('controlPlaneSubnet', watchedControlPlaneSubnet)
		setFormValues('secondaryPodsRange', watchedSecondaryPodsRange)
		setFormValues('secondaryServicesRange', watchedSecondaryServicesRange)
		setFormValues(
			'primarySubnetResourceType',
			editablePrimarySubnetResourceType
		)
		setFormValues('additionalSubnets', editableAdditionalSubnets)
		if (!isGke) {
			setFormValues('nonGkeAddSubnets', nonGkeAddSubnets)
		}
	}, [
		editableAdditionalSubnets,
		editablePrimarySubnetResourceType,
		isGke,
		nonGkeAddSubnets,
		setFormValues,
		watchedControlPlaneSubnet,
		watchedPrimarySubnet,
		watchedSecondaryPodsRange,
		watchedSecondaryServicesRange
	])

	const clearNonGkeSubnetFields = () => {
		reset({
			primarySubnet: '',
			controlPlaneSubnet: '',
			secondaryPodsRange: '',
			secondaryServicesRange: ''
		})
		setEditableAdditionalSubnets([])
		setEditablePrimarySubnetResourceType('')
		setFormValues('primarySubnet', '')
		setFormValues('controlPlaneSubnet', '')
		setFormValues('secondaryPodsRange', '')
		setFormValues('secondaryServicesRange', '')
		setFormValues('primarySubnetResourceType', '')
		setFormValues('additionalSubnets', [])
	}

	const handleNonGkeAddSubnetsChange = (value: 'yes' | 'no') => {
		setNonGkeAddSubnets(value)
		setFormValues('nonGkeAddSubnets', value)
		if (value === 'no') {
			clearNonGkeSubnetFields()
		}
	}

	useEffect(() => {
		if (!embeddedInApproval) {
			return
		}
		syncApprovalFormValues()
	}, [embeddedInApproval, syncApprovalFormValues])

	useEffect(() => {
		if (!embeddedInApproval || !onSubnetValidityChange) {
			return
		}

		onSubnetValidityChange(
			validateSubnetDetailsForApproval({
				infrastructureType,
				nonGkeAddSubnets,
				primarySubnet: watchedPrimarySubnet,
				controlPlaneSubnet: watchedControlPlaneSubnet,
				secondaryPodsRange: watchedSecondaryPodsRange,
				secondaryServicesRange: watchedSecondaryServicesRange,
				primarySubnetResourceType: editablePrimarySubnetResourceType,
				additionalSubnets: editableAdditionalSubnets
			})
		)
	}, [
		editableAdditionalSubnets,
		editablePrimarySubnetResourceType,
		embeddedInApproval,
		infrastructureType,
		nonGkeAddSubnets,
		onSubnetValidityChange,
		watchedControlPlaneSubnet,
		watchedPrimarySubnet,
		watchedSecondaryPodsRange,
		watchedSecondaryServicesRange
	])

	const renderReadOnlyField = (label: string, value: string) => (
		<div>
			<Box variant="awsui-key-label">{label}</Box>
			{value || 'NA'}
		</div>
	)

	const renderAdditionalSubnetsEdit = (optional = false) => (
		<SpaceBetween direction="vertical" size="s">
			<Box variant="awsui-key-label">
				Additional Subnets{optional ? ' (optional)' : ''}
			</Box>
			{editableAdditionalSubnets.map((subnet, index) => {
				const cidrError =
					subnet.cidr.trim() !== ''
						? getSubnetCidrError(
								subnet.cidr,
								subnet.resourceType,
								`Additional Subnet ${index + 1}`
						  )
						: undefined
				const resourceTypeError =
					subnet.cidr.trim() !== '' && subnet.resourceType.trim() === ''
						? 'Resource type is required'
						: undefined

				return (
					<div
						key={`additional-subnet-${index}`}
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr auto',
							gap: '12px',
							alignItems: 'start'
						}}
					>
						<FormField
							label={`Additional Subnet ${index + 1}`}
							description="Optional additional subnet CIDR range"
							constraintText="Format: CIDR notation (e.g., 10.0.1.0/24)"
							errorText={cidrError}
						>
							<Input
								value={subnet.cidr}
								placeholder="e.g., 10.0.1.0/24"
								onChange={({ detail }) => {
									const next = [...editableAdditionalSubnets]
									next[index] = { ...next[index], cidr: detail.value }
									setEditableAdditionalSubnets(next)
								}}
							/>
						</FormField>
						<FormField
							label="Resource Type"
							description="Select the GCP resource type"
							errorText={resourceTypeError}
						>
							<Select
								selectedOption={
									subnet.resourceType
										? resourceTypeOptions.find(
												(o) => o.value === subnet.resourceType
										  ) || null
										: null
								}
								options={resourceTypeOptions}
								placeholder="Select resource type"
								filteringType="auto"
								onChange={({ detail }) => {
									const next = [...editableAdditionalSubnets]
									next[index] = {
										...next[index],
										resourceType: detail.selectedOption.value || ''
									}
									setEditableAdditionalSubnets(next)
								}}
							/>
						</FormField>
						<Box padding={{ bottom: 's' }}>
							<Button
								variant="link"
								iconName="remove"
								ariaLabel={`Remove additional subnet ${index + 1}`}
								onClick={() => {
									setEditableAdditionalSubnets(
										editableAdditionalSubnets.filter(
											(_, itemIndex) => itemIndex !== index
										)
									)
								}}
							/>
						</Box>
					</div>
				)
			})}
			<Button
				iconName="add-plus"
				onClick={() => {
					setEditableAdditionalSubnets([
						...editableAdditionalSubnets,
						{ cidr: '', resourceType: '' }
					])
				}}
			>
				Add Additional Subnet
			</Button>
		</SpaceBetween>
	)

	const renderGkeFields = () => (
		<>
			<FormInput
				name="primarySubnet"
				label={primarySubnetLabel}
				placeholder="e.g., 10.0.0.0/24"
				constraintText="Format: CIDR notation (e.g., 10.0.0.0/24)"
				control={control}
				rules={cidrRules(primarySubnetLabel)}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="controlPlaneSubnet"
				label="Control Plane Subnet"
				placeholder="e.g., 10.0.1.0/28"
				constraintText="Format: CIDR notation (e.g., 10.0.1.0/28)"
				control={control}
				rules={cidrRules('Control Plane Subnet')}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="secondaryPodsRange"
				label="Secondary Range for Pods"
				placeholder="e.g., 10.1.0.0/16"
				constraintText="Format: CIDR notation (e.g., 10.1.0.0/16)"
				control={control}
				rules={cidrRules('Secondary Range for Pods')}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="secondaryServicesRange"
				label="Secondary Range for Services"
				placeholder="e.g., 10.2.0.0/20"
				constraintText="Format: CIDR notation (e.g., 10.2.0.0/20)"
				control={control}
				rules={cidrRules('Secondary Range for Services')}
				setFormValues={setFormValues}
			/>
			{renderAdditionalSubnetsEdit()}
		</>
	)

	const renderNonGkeFields = () => (
		<>
			<FormField label="Do you want to add subnet details?">
				<RadioGroup
					value={nonGkeAddSubnets}
					items={[
						{ value: 'no', label: 'No' },
						{ value: 'yes', label: 'Yes' }
					]}
					onChange={({ detail }) => {
						handleNonGkeAddSubnetsChange(detail.value as 'yes' | 'no')
					}}
				/>
			</FormField>
			{showNonGkeSubnetFields && (
				<>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr',
							gap: '12px',
							alignItems: 'start'
						}}
					>
						<FormInput
							name="primarySubnet"
							label={primarySubnetLabel}
							placeholder="e.g., 10.0.0.0/24"
							constraintText="Format: CIDR notation (e.g., 10.0.0.0/24)"
							control={control}
							rules={cidrRules(
								primarySubnetLabel,
								true,
								editablePrimarySubnetResourceType
							)}
							setFormValues={setFormValues}
						/>
						<FormField
							label="Resource Type"
							description="Select the GCP resource type for this subnet"
							errorText={
								(watchedPrimarySubnet || '').trim() !== '' &&
								!editablePrimarySubnetResourceType
									? 'Resource type is required'
									: undefined
							}
						>
							<Select
								selectedOption={
									editablePrimarySubnetResourceType
										? resourceTypeOptions.find(
												(o) => o.value === editablePrimarySubnetResourceType
										  ) || null
										: null
								}
								options={resourceTypeOptions}
								placeholder="Select resource type"
								filteringType="auto"
								onChange={({ detail }) => {
									setEditablePrimarySubnetResourceType(
										detail.selectedOption.value || ''
									)
								}}
							/>
						</FormField>
					</div>
					{renderAdditionalSubnetsEdit(true)}
				</>
			)}
		</>
	)

	const renderReadOnlyView = () => (
		<ColumnLayout columns={2} variant="text-grid">
			{renderReadOnlyField('Infrastructure Type', infrastructureType)}
			{hasSavedSubnets || isGke ? (
				<>
					{renderReadOnlyField(primarySubnetLabel, defaultValues.primarySubnet)}
					{!isGke &&
						renderReadOnlyField(
							'Primary Subnet Resource Type',
							savedPrimarySubnetResourceType || 'NA'
						)}
					{isGke && (
						<>
							{renderReadOnlyField(
								'Control Plane Subnet',
								defaultValues.controlPlaneSubnet
							)}
							{renderReadOnlyField(
								'Secondary Range for Pods',
								defaultValues.secondaryPodsRange
							)}
							{renderReadOnlyField(
								'Secondary Range for Services',
								defaultValues.secondaryServicesRange
							)}
						</>
					)}
					{savedAdditionalSubnets.length > 0 && (
						<div>
							<Box variant="awsui-key-label">Additional Subnets</Box>
							{savedAdditionalSubnets.map((subnet, index) => (
								<div key={index}>
									{subnet.cidr}
									{subnet.resourceType ? ` (${subnet.resourceType})` : ''}
								</div>
							))}
						</div>
					)}
				</>
			) : (
				<Box color="text-body-secondary" fontSize="body-s">
					No subnet details configured.
				</Box>
			)}
		</ColumnLayout>
	)

	return (
		<Container header={<Header variant="h3">Subnet Details</Header>}>
			<SpaceBetween direction="vertical" size="l">
				{embeddedInApproval ? (
					<SpaceBetween direction="vertical" size="l">
						<FormField label="Infrastructure Type">
							<Box>{infrastructureType}</Box>
						</FormField>
						{isGke ? renderGkeFields() : renderNonGkeFields()}
					</SpaceBetween>
				) : (
					renderReadOnlyView()
				)}
			</SpaceBetween>
		</Container>
	)
}

export default GcpProjectSubnetDetails
